import { WorkbookCacheKey, WritableCacheKey } from "./cacheKeys";
export type cacheableData = Record<string, boolean | string | number | object | string[]>;

type MirrorEntry = { partIds: string[]; value: string };

const REPORT_OPTION_PREFIX = "reportOption-";
const EDIPHI_XML_NAMESPACE = "http://excel.ediphi.com/cache";

export const worksheetCache = {
  async setOptions(worksheet: Excel.Worksheet, options: cacheableData): Promise<void> {
    const customProperties = worksheet.customProperties.load("items/key,value");
    await worksheet.context.sync();

    for (const key in options) {
      const propertyName = `${REPORT_OPTION_PREFIX}${key}`;
      const serializedValue = JSON.stringify(options[key]);
      const existingProperty = customProperties.items.find((item) => item.key === propertyName);
      if (existingProperty) {
        existingProperty.value = serializedValue;
      } else {
        customProperties.add(propertyName, serializedValue);
      }
    }

    await worksheet.context.sync();
  },

  async deleteOption(key: string): Promise<void> {
    return Excel.run(async (context) => {
      try {
        const propertyName = `${REPORT_OPTION_PREFIX}${key}`;
        const worksheets = context.workbook.worksheets;
        worksheets.load("items");
        await context.sync();

        for (const worksheet of worksheets.items) {
          worksheet.load("customProperties");
          await worksheet.context.sync();

          const customProperties = worksheet.customProperties.load("items/key,value");
          await worksheet.context.sync();

          const property = customProperties.items.find((item) => item.key === propertyName);
          if (property) {
            property.delete();
          }
        }

        await context.sync();
      } catch (error) {
        console.warn("WorksheetCache deleteOption operation failed:", { key }, error);
      }
    });
  },

  async getOptions(worksheet: Excel.Worksheet): Promise<cacheableData> {
    worksheet.load("customProperties");
    await worksheet.context.sync();

    const customProperties = worksheet.customProperties.load("items/key,value");
    await worksheet.context.sync();

    return optionsFromProperties(customProperties.items);
  },

  async clearOptions(worksheet: Excel.Worksheet): Promise<void> {
    worksheet.load("customProperties");
    await worksheet.context.sync();

    for (const property of worksheet.customProperties.items) {
      if (property.key.startsWith(REPORT_OPTION_PREFIX)) {
        property.delete();
      }
    }

    await worksheet.context.sync();
  },
};

/** A sheet's report options, read from custom properties already loaded. */
export const optionsFromProperties = (properties: { key: string; value: string }[]): cacheableData => {
  const options: cacheableData = {};

  for (const property of properties) {
    if (property.key.startsWith(REPORT_OPTION_PREFIX)) {
      const key = property.key.replace(REPORT_OPTION_PREFIX, "");
      try {
        options[key] = JSON.parse(property.value);
      } catch {
        options[key] = property.value; // Fallback if not valid JSON
      }
    }
  }

  return options;
};

let mirror: Map<string, MirrorEntry> | null = null;

// Every workbookCache method opens an Excel.run that read-modify-writes the mirror and the parts
// behind it, and some are fired without being awaited (saving the card order, for one). One
// promise chain runs them in call order, so two writes of a new key can't both add a part. This is
// a safety net, not a way to parallelise: write several keys with one `write` call instead.
let cacheOpChain: Promise<unknown> = Promise.resolve();

const parseStoredValue = (value: string): any => {
  try {
    return JSON.parse(value);
  } catch {
    return value;
  }
};

const readEntry = (xml: string): { key: string; value: string } | null => {
  try {
    const root = new DOMParser().parseFromString(xml, "text/xml").documentElement;
    const key = root?.getAttribute("key");
    return key ? { key, value: root.textContent ?? "" } : null;
  } catch (error) {
    console.warn("WorkbookCache XML parsing failed:", error);
    return null;
  }
};

const loadMirror = async (context: Excel.RequestContext): Promise<Map<string, MirrorEntry>> => {
  if (mirror) return mirror;
  const parts = context.workbook.customXmlParts.getByNamespace(EDIPHI_XML_NAMESPACE);
  parts.load("items/id");
  await context.sync();

  const xmlResults = parts.items.map((part) => part.getXml());
  await context.sync();

  const loaded = new Map<string, MirrorEntry>();
  parts.items.forEach((part, index) => {
    const entry = readEntry(xmlResults[index].value);
    if (!entry) return;
    const existing = loaded.get(entry.key);
    if (existing) existing.partIds.push(part.id);
    else loaded.set(entry.key, { partIds: [part.id], value: entry.value });
  });
  mirror = loaded;
  return loaded;
};

const workbookCacheRaw = {
  async get(key: WorkbookCacheKey) {
    return Excel.run(async (context) => {
      try {
        const value = (await loadMirror(context)).get(key)?.value;
        return value ? parseStoredValue(value) : null;
      } catch (error) {
        /// ???: Change to logger.warn? Should this throw instead of swallowing?
        console.warn("WorkbookCache get operation failed:", { key }, error);
        return null;
      }
    });
  },

  async write({
    set: entries = {},
    delete: deletions = [],
  }: {
    set?: Partial<Record<WritableCacheKey, any>>;
    delete?: WorkbookCacheKey[];
  }) {
    return Excel.run(async (context) => {
      try {
        const loaded = await loadMirror(context);
        const customXmlParts = context.workbook.customXmlParts;
        const added: Array<{ key: string; part: Excel.CustomXmlPart }> = [];
        const written = new Map<string, MirrorEntry>();
        const deleted = deletions.filter((key) => loaded.get(key)?.partIds.length);

        for (const [key, value] of Object.entries(entries)) {
          const valueToStore =
            typeof value === "string" || typeof value === "boolean" ? value.toString() : JSON.stringify(value);
          const xmlString = `<ediphi_cache xmlns='${EDIPHI_XML_NAMESPACE}' key='${key}'>${escapeXml(valueToStore)}</ediphi_cache>`;

          const [existingPartId, ...duplicateIds] = loaded.get(key)?.partIds ?? [];
          if (existingPartId) {
            // Update in place — a failed sync can never leave two parts holding the same key
            customXmlParts.getItem(existingPartId).setXml(xmlString);
            written.set(key, { partIds: [existingPartId], value: valueToStore });
          } else {
            const part = customXmlParts.add(xmlString);
            part.load("id");
            added.push({ key, part });
            written.set(key, { partIds: [], value: valueToStore });
          }
          for (const duplicateId of duplicateIds) {
            customXmlParts.getItem(duplicateId).delete();
          }
        }
        for (const key of deleted) {
          for (const partId of loaded.get(key)!.partIds) {
            customXmlParts.getItemOrNullObject(partId).delete();
          }
        }
        if (!written.size && !deleted.length) return;
        await context.sync();

        for (const { key, part } of added) written.get(key)!.partIds.push(part.id);
        written.forEach((entry, key) => loaded.set(key, entry));
        deleted.forEach((key) => loaded.delete(key));
      } catch (error) {
        mirror = null;
        /// ???: Change to logger.warn? Should this throw instead of swallowing?
        console.warn("WorkbookCache write operation failed:", { set: Object.keys(entries), delete: deletions }, error);
      }
    });
  },

  set(entries: Partial<Record<WritableCacheKey, any>>) {
    return workbookCacheRaw.write({ set: entries });
  },

  delete(key: WorkbookCacheKey) {
    return workbookCacheRaw.write({ delete: [key] });
  },

  async getMany(keys: string[]): Promise<Record<string, any>> {
    return Excel.run(async (context) => {
      const loaded = await loadMirror(context);
      const result: Record<string, any> = {};
      for (const key of keys) {
        const value = loaded.get(key)?.value;
        if (value !== undefined) result[key] = parseStoredValue(value);
      }
      return result;
    }).catch(() => ({}));
  },
};

const serializeCacheOp = <T>(op: () => Promise<T>): Promise<T> => {
  const run = cacheOpChain.then(op, op);
  // A rejected or fire-and-forget op must not break the chain for the next caller.
  cacheOpChain = run.then(
    () => undefined,
    () => undefined
  );
  return run;
};

export const workbookCache = {
  get: (key: WorkbookCacheKey) => serializeCacheOp(() => workbookCacheRaw.get(key)),
  set: (entries: Partial<Record<WritableCacheKey, any>>) => serializeCacheOp(() => workbookCacheRaw.set(entries)),
  write: (changes: { set?: Partial<Record<WritableCacheKey, any>>; delete?: WorkbookCacheKey[] }) =>
    serializeCacheOp(() => workbookCacheRaw.write(changes)),
  delete: (key: WorkbookCacheKey) => serializeCacheOp(() => workbookCacheRaw.delete(key)),
  getMany: (keys: string[]) => serializeCacheOp(() => workbookCacheRaw.getMany(keys)),
};

// Utility function to escape XML characters
function escapeXml(unsafe: string) {
  return unsafe.replace(
    /[<>&"']/g,
    (c) =>
      ({
        "<": "&lt;",
        ">": "&gt;",
        "&": "&amp;",
        '"': "&quot;",
        "'": "&apos;",
      })[c] || ""
  );
}
